import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TICKET_PRIORITIES,
  type CreateTicketInput,
  type Ticket,
  type TicketOrigin,
  type TicketPriority,
  type TimeEntry,
} from '@catalyst-psa/shared';

import { ApiError, mutationError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { formatDate, formatDateTime } from '../../utils/date.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { useProjectOptions, useProjectPhases } from '../projects/api.js';
import { useAgreements } from '../agreements/api.js';
import { useContacts } from '../contacts/api.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useCustomer } from '../customers/api.js';
import {
  useBillingClassOptions,
  useServiceCategoryOptions,
  useTimeEntries,
} from '../time-entries/api.js';
import {
  BILLING_STATUS_FILTERS,
  billingStatusOf,
  matchesBillingStatusFilter,
} from '../time-entries/billing-status.js';
import { TimeEntryFormModal } from '../time-entries/TimeEntryFormModal.js';
import { TicketDiagnosticsModal } from './TicketDiagnosticsModal.js';
import { ticketDisplayId } from './reference.js';
import {
  useAssignableMembers,
  useCreateTicket,
  useDeleteTicket,
  useRegenerateTicketPublicLink,
  useTicketStatusOptions,
  useTicketTypeOptions,
  useUpdateTicket,
} from './api.js';

interface TicketFormModalProps {
  isOpen: boolean;
  ticket: Ticket | null;
  /** When creating, pre-fill a project ticket for this project. */
  initialProjectId?: string;
  /** When creating, pre-fill the phase (only meaningful with initialProjectId). */
  initialPhaseId?: string | null;
  onClose: () => void;
}

interface FormState {
  origin: TicketOrigin;
  projectId: string;
  phaseId: string;
  customerId: string;
  agreementId: string;
  ticketTypeId: string;
  contactId: string;
  statusId: string;
  title: string;
  description: string;
  priority: TicketPriority;
  budgetHours: string;
  cuTaskId: string;
  cuTaskLink: string;
  assigneeMemberIds: string[];
}

const EMPTY_FORM: FormState = {
  origin: 'support',
  projectId: '',
  phaseId: '',
  customerId: '',
  agreementId: '',
  ticketTypeId: '',
  contactId: '',
  statusId: '',
  title: '',
  description: '',
  priority: 'medium',
  budgetHours: '',
  cuTaskId: '',
  cuTaskLink: '',
  assigneeMemberIds: [],
};

function toFormState(
  ticket: Ticket | null,
  initialProjectId?: string,
  initialPhaseId?: string | null,
): FormState {
  if (!ticket) {
    if (initialProjectId) {
      return {
        ...EMPTY_FORM,
        origin: 'project',
        projectId: initialProjectId,
        phaseId: initialPhaseId ?? '',
      };
    }
    return EMPTY_FORM;
  }
  return {
    origin: ticket.origin,
    projectId: ticket.projectId ?? '',
    phaseId: ticket.phaseId ?? '',
    customerId: ticket.customerId ?? '',
    agreementId: ticket.agreementId ?? '',
    ticketTypeId: ticket.ticketTypeId ?? '',
    contactId: ticket.contactId ?? '',
    statusId: ticket.statusId,
    title: ticket.title,
    description: ticket.description ?? '',
    priority: ticket.priority,
    budgetHours: ticket.budgetHours ?? '',
    cuTaskId: ticket.cuTaskId ?? '',
    cuTaskLink: ticket.cuTaskLink ?? '',
    assigneeMemberIds: ticket.assigneeMemberIds,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

const PRIORITY_OPTIONS = TICKET_PRIORITIES.map((priority) => ({
  value: priority,
  label: priority.charAt(0).toUpperCase() + priority.slice(1),
}));

const ORIGIN_OPTIONS: Array<{ value: TicketOrigin; label: string }> = [
  { value: 'support', label: 'Support (customer)' },
  { value: 'project', label: 'Project' },
];

export function TicketFormModal({
  isOpen,
  ticket: ticketProp,
  initialProjectId,
  initialPhaseId,
  onClose,
}: TicketFormModalProps): JSX.Element {
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<Ticket | null>(ticketProp);
  const isEdit = ticket !== null;
  const { can, principal } = useAuth();
  const currentMemberId = principal?.type === 'member' ? principal.id : undefined;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() =>
    toFormState(ticket, initialProjectId, initialPhaseId),
  );
  // Support and project tickets carry independent RBAC areas, so edit/delete affordances
  // follow the ticket's origin (e.g. a member may edit project tickets but not support).
  const originArea = form.origin === 'project' ? 'projectTickets' : 'supportTickets';
  const canDelete = can(originArea, 'delete');
  const canEdit = can(originArea, 'edit');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [hourModalOpen, setHourModalOpen] = useState(false);
  const [editingHour, setEditingHour] = useState<TimeEntry | null>(null);
  const [tab, setTab] = useState<'details' | 'hours' | 'history'>('details');
  const [linkCopied, setLinkCopied] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  // Kept outside `ticket` so generating a link doesn't re-run the form-reset effect
  // (which would wipe unsaved edits).
  const [publicToken, setPublicToken] = useState<string | null>(ticketProp?.publicToken ?? null);

  const createMutation = useCreateTicket();
  const updateMutation = useUpdateTicket();
  const deleteMutation = useDeleteTicket();
  const regenerateLink = useRegenerateTicketPublicLink();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  const statusesQuery = useTicketStatusOptions();
  const typesQuery = useTicketTypeOptions();
  const membersQuery = useAssignableMembers();
  const projectsQuery = useProjectOptions(isOpen);
  const selectedSupportCustomerQuery = useCustomer(
    isOpen && form.origin === 'support' ? form.customerId || undefined : undefined,
  );
  const supportAgreementsQuery = useAgreements(
    {
      page: 1,
      pageSize: 200,
      customerId: form.origin === 'support' ? form.customerId || undefined : undefined,
      sortBy: 'name',
      sortDir: 'asc',
    },
    {
      enabled: isOpen && form.origin === 'support' && Boolean(form.customerId),
    },
  );

  const statusOptions = useMemo(
    () => (statusesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [statusesQuery.data],
  );
  const typeOptions = useMemo(
    () => (typesQuery.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    [typesQuery.data],
  );
  const projectOptions = useMemo(() => {
    const opts = (projectsQuery.data ?? []).map((p) => ({
      value: p.id,
      label: `#${p.number} — ${p.name} (${p.customerName})`,
    }));
    // Safety net: if the linked project somehow isn't in the options list, still show it
    // so the native <select> matches the ticket's real projectId instead of falling back
    // to its first option.
    if (ticket?.projectId && !opts.some((o) => o.value === ticket.projectId)) {
      opts.unshift({
        value: ticket.projectId,
        label:
          ticket.projectNumber != null
            ? `#${ticket.projectNumber} — ${ticket.projectName ?? ''}`.trim()
            : (ticket.projectName ?? 'Current project'),
      });
    }
    return opts;
  }, [projectsQuery.data, ticket]);
  const phasesQuery = useProjectPhases(form.origin === 'project' ? form.projectId : '');
  const phaseOptions = useMemo(
    () => [
      { value: '', label: 'No phase' },
      ...(phasesQuery.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    ],
    [phasesQuery.data],
  );
  const supportDefaultAgreementId =
    selectedSupportCustomerQuery.data?.defaultSupportAgreementId ?? '';
  const supportDefaultAgreementName = useMemo(() => {
    if (!supportDefaultAgreementId) return null;
    const agreement = (supportAgreementsQuery.data?.data ?? []).find(
      (item) => item.id === supportDefaultAgreementId,
    );
    return agreement ? `#${agreement.number} - ${agreement.name}` : null;
  }, [supportAgreementsQuery.data, supportDefaultAgreementId]);
  const members = useMemo(() => membersQuery.data ?? [], [membersQuery.data]);

  // The ticket's customer is derived from the selected project or agreement; contacts
  // are scoped to that customer.
  const customerId = useMemo(() => {
    if (form.origin === 'project' && form.projectId) {
      const fromList = (projectsQuery.data ?? []).find((p) => p.id === form.projectId)?.customerId;
      // Fall back to the ticket's own resolved customer when its project isn't loaded,
      // so contacts still scope correctly.
      return (
        fromList ??
        (ticket?.projectId === form.projectId ? (ticket.customerId ?? undefined) : undefined)
      );
    }
    if (form.origin === 'support' && form.customerId) {
      return form.customerId;
    }
    return undefined;
  }, [form.origin, form.projectId, form.customerId, projectsQuery.data, ticket]);

  const contactsQuery = useContacts({ customerId, isActive: true });
  const contactOptions = useMemo(
    () => [
      { value: '', label: 'No contact' },
      ...(contactsQuery.data?.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.firstName} ${c.lastName ?? ''}`.trim(),
      })),
    ],
    [contactsQuery.data],
  );

  // Hours logged on this ticket (only meaningful when editing an existing ticket).
  const hoursQuery = useTimeEntries(
    isEdit && ticket ? { page: 1, pageSize: 200, ticketId: ticket.id } : { page: 1, pageSize: 1 },
  );
  const ticketHours = isEdit ? (hoursQuery.data?.data ?? []) : [];

  // Name lookups so each hour row can show its member, service category and billing
  // class. Members reuse the assignable-members list already loaded above.
  const hourCategoriesQuery = useServiceCategoryOptions();
  const hourClassesQuery = useBillingClassOptions();
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) map.set(member.id, member.displayName);
    return map;
  }, [members]);
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of hourCategoriesQuery.data ?? []) map.set(category.id, category.name);
    return map;
  }, [hourCategoriesQuery.data]);
  const classNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const billingClass of hourClassesQuery.data ?? [])
      map.set(billingClass.id, billingClass.name);
    return map;
  }, [hourClassesQuery.data]);

  // Hours-tab filters: a text search across every displayed field + a billing-status
  // dropdown. Applied client-side over the loaded ticket hours.
  const [hourSearch, setHourSearch] = useState('');
  const [hourStatusFilter, setHourStatusFilter] = useState('all');
  const filteredHours = useMemo(() => {
    const term = hourSearch.trim().toLowerCase();
    return ticketHours.filter((hour) => {
      if (!matchesBillingStatusFilter(hour, hourStatusFilter)) return false;
      if (!term) return true;
      const status = billingStatusOf(hour);
      const haystack = [
        formatDate(hour.workDate),
        `${hour.hours}`,
        memberNames.get(hour.memberId) ?? hour.memberName ?? '',
        categoryNames.get(hour.serviceCategoryId) ?? hour.serviceCategoryName ?? '',
        classNames.get(hour.billingClassId) ?? hour.billingClassName ?? '',
        hour.notes ?? '',
        status.label,
        status.detail ?? '',
        hour.invoiceNumber ?? '',
        hour.externalInvoiceRef ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [ticketHours, hourSearch, hourStatusFilter, memberNames, categoryNames, classNames]);

  const selectedMembers = useMemo(
    () =>
      form.assigneeMemberIds
        .map((id) => members.find((m) => m.id === id))
        .filter((m): m is (typeof members)[number] => Boolean(m)),
    [form.assigneeMemberIds, members],
  );
  const filteredMembers = useMemo(() => {
    const q = assigneeSearch.trim().toLowerCase();
    return members.filter(
      (m) =>
        !form.assigneeMemberIds.includes(m.id) &&
        (q === '' || m.displayName.toLowerCase().includes(q)),
    );
  }, [members, form.assigneeMemberIds, assigneeSearch]);

  useEffect(() => {
    if (isOpen) {
      setTicket(ticketProp);
    }
  }, [isOpen, ticketProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(ticket, initialProjectId, initialPhaseId));
      setPublicToken(ticket?.publicToken ?? null);
      setConfirmOpen(false);
      setAssigneeSearch('');
      setTab('details');
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, ticket, initialProjectId, initialPhaseId, resetSaveMode, resetCreate, resetUpdate]);

  useEffect(() => {
    if (!isOpen || form.origin !== 'support' || !form.customerId || form.agreementId) {
      return;
    }
    if (supportDefaultAgreementId) {
      setForm((f) => ({
        ...f,
        agreementId: supportDefaultAgreementId,
      }));
    }
  }, [isOpen, form.origin, form.customerId, form.agreementId, supportDefaultAgreementId]);

  // Default the status to the configured default when creating.
  useEffect(() => {
    if (isOpen && !isEdit && !form.statusId && statusesQuery.data?.length) {
      const def = statusesQuery.data.find((s) => s.isDefault) ?? statusesQuery.data[0];
      if (def) {
        setForm((f) => (f.statusId ? f : { ...f, statusId: def.id }));
      }
    }
  }, [isOpen, isEdit, form.statusId, statusesQuery.data]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save ticket.'
        : null;

  const toggleAssignee = (memberId: string): void => {
    setForm((f) => ({
      ...f,
      assigneeMemberIds: f.assigneeMemberIds.includes(memberId)
        ? f.assigneeMemberIds.filter((id) => id !== memberId)
        : [...f.assigneeMemberIds, memberId],
    }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    if (isEdit) {
      const input: Parameters<typeof updateMutation.mutate>[0]['input'] = {
        title: form.title.trim(),
        description: form.description.trim() ? form.description.trim() : null,
        priority: form.priority,
        statusId: form.statusId || undefined,
        contactId: form.contactId || null,
        assigneeMemberIds: form.assigneeMemberIds,
        budgetHours: form.budgetHours.trim() ? form.budgetHours.trim() : null,
        cuTaskId: form.cuTaskId.trim() ? form.cuTaskId.trim() : null,
        cuTaskLink: form.cuTaskLink.trim() ? form.cuTaskLink.trim() : null,
      };
      if (form.origin === 'project') {
        input.projectId = form.projectId || undefined;
        input.phaseId = form.phaseId || null;
      } else {
        input.customerId = form.customerId || undefined;
        input.agreementId = form.agreementId || undefined;
        input.ticketTypeId = form.ticketTypeId || undefined;
      }
      updateMutation.mutate(
        { id: ticket.id, input },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
      return;
    }

    const payload: CreateTicketInput = {
      origin: form.origin,
      title: form.title.trim(),
      description: form.description.trim() ? form.description.trim() : null,
      priority: form.priority,
      statusId: form.statusId || undefined,
      contactId: form.contactId || null,
      assigneeMemberIds: form.assigneeMemberIds,
      budgetHours: form.budgetHours.trim() ? form.budgetHours.trim() : null,
      cuTaskId: form.cuTaskId.trim() ? form.cuTaskId.trim() : null,
      cuTaskLink: form.cuTaskLink.trim() ? form.cuTaskLink.trim() : null,
      ...(form.origin === 'project'
        ? { projectId: form.projectId || undefined, phaseId: form.phaseId || null }
        : {
            customerId: form.customerId || undefined,
            agreementId: form.agreementId || undefined,
            ticketTypeId: form.ticketTypeId || undefined,
          }),
    };
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        if (saveMode.closeAfterSaveRef.current) {
          onClose();
        } else {
          setTicket(created);
        }
      },
    });
  };

  const handleDelete = (): void => {
    if (!ticket) {
      return;
    }
    deleteMutation.mutate(ticket.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  const goToCustomer = (): void => {
    const targetCustomerId = customerId || ticket?.customerId;
    if (!targetCustomerId) {
      return;
    }
    navigate(`/customers?returnCustomerId=${targetCustomerId}&returnTab=tickets`);
  };

  const linkedProjectId = form.projectId || ticket?.projectId || '';
  const linkedAgreementId = form.agreementId || ticket?.agreementId || '';

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? `Ticket ${ticketDisplayId(ticket)}` : 'New ticket'}
        size="wide"
        headerActions={
          <div className="flex items-center gap-2">
            {isEdit ? (
              <Button variant="secondary" size="sm" onClick={() => setDiagnosticsOpen(true)}>
                Technical history
              </Button>
            ) : null}
            {linkedProjectId ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/projects/${linkedProjectId}`)}
              >
                Go to Project
              </Button>
            ) : null}
            {linkedAgreementId ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/agreements/${linkedAgreementId}`)}
              >
                Go to Agreement
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={goToCustomer}
              disabled={!customerId && !ticket?.customerId}
            >
              Go to Customer
            </Button>
          </div>
        }
        tabs={
          isEdit
            ? [
                { id: 'details', label: 'Details' },
                { id: 'hours', label: `Hours${ticket ? ` · ${ticket.actualHours}` : ''}` },
                { id: 'history', label: 'History' },
              ]
            : undefined
        }
        activeTabId={tab}
        onTabChange={(id) => setTab(id as 'details' | 'hours' | 'history')}
        footer={
          <FormActions
            formId="ticket-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && canDelete ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form
          id="ticket-form"
          onSubmit={handleSubmit}
          className={`flex flex-col gap-4 ${isEdit && tab !== 'details' ? 'hidden' : ''}`}
        >
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          {isEdit && ticket?.statusIsClosed ? (
            ticket.timeEntryLocked ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                Closed — time tracking is locked. New time entries can no longer be added to this
                ticket.
              </div>
            ) : ticket.timeEntryLockAt ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Closed — time entries can still be added until{' '}
                {formatDateTime(ticket.timeEntryLockAt)} (this week&apos;s timesheet deadline), then
                the ticket locks for time tracking.
              </div>
            ) : null
          ) : null}

          {form.origin === 'project' ? (
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Project"
                required
                value={form.projectId}
                placeholder={projectsQuery.isLoading ? 'Loading…' : 'Select a project'}
                options={projectOptions}
                onChange={(e) =>
                  setForm((f) => ({ ...f, projectId: e.target.value, phaseId: '', contactId: '' }))
                }
                error={fieldErrors.projectId}
              />
              <SelectField
                label="Phase"
                value={form.phaseId}
                disabled={!form.projectId}
                options={phaseOptions}
                onChange={(e) => setForm((f) => ({ ...f, phaseId: e.target.value }))}
                error={fieldErrors.phaseId}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <CustomerSelectField
                label="Customer"
                required
                value={form.customerId}
                onChange={(nextCustomerId) =>
                  setForm((f) => ({
                    ...f,
                    customerId: nextCustomerId,
                    agreementId: '',
                    contactId: '',
                  }))
                }
                activeOnly
                placeholder="Select a customer"
                error={fieldErrors.customerId}
              />
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Default support agreement
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  {form.customerId
                    ? (supportDefaultAgreementName ?? 'No default support agreement configured')
                    : 'Select a customer first'}
                </p>
              </div>
            </div>
          )}

          <TextField
            label="Title"
            required
            value={form.title}
            maxLength={300}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            error={fieldErrors.title}
          />

          <TextAreaField
            label="Description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            error={fieldErrors.description}
          />

          <div className="grid grid-cols-3 gap-4">
            <TextField
              label="Budget hours"
              type="number"
              min="0"
              step="0.25"
              value={form.budgetHours}
              onChange={(e) => setForm((f) => ({ ...f, budgetHours: e.target.value }))}
              error={fieldErrors.budgetHours}
            />
            <TextField
              label="CU Task ID"
              value={form.cuTaskId}
              maxLength={128}
              onChange={(e) => setForm((f) => ({ ...f, cuTaskId: e.target.value }))}
              error={fieldErrors.cuTaskId}
            />
            <div>
              <TextField
                label="CU Task Link"
                type="url"
                value={form.cuTaskLink}
                maxLength={1024}
                placeholder="https://app.clickup.com/…"
                onChange={(e) => setForm((f) => ({ ...f, cuTaskLink: e.target.value }))}
                error={fieldErrors.cuTaskLink}
              />
              {/^https?:\/\//i.test(form.cuTaskLink.trim()) ? (
                <a
                  href={form.cuTaskLink.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"
                >
                  Open link
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-3 w-3"
                  >
                    <path
                      d="M14 5h5v5M19 5l-9 9M11 5H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Status"
              value={form.statusId}
              placeholder={statusesQuery.isLoading ? 'Loading…' : 'Select a status'}
              options={statusOptions}
              onChange={(e) => setForm((f) => ({ ...f, statusId: e.target.value }))}
              error={fieldErrors.statusId}
            />
            <SelectField
              label="Priority"
              value={form.priority}
              options={PRIORITY_OPTIONS}
              onChange={(e) =>
                setForm((f) => ({ ...f, priority: e.target.value as TicketPriority }))
              }
              error={fieldErrors.priority}
            />
          </div>

          {isEdit && ticket ? (
            <div className="grid grid-cols-3 gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Created
                </p>
                <p className="mt-1 text-sm text-slate-700">{formatDateTime(ticket.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Last updated
                </p>
                <p className="mt-1 text-sm text-slate-700">{formatDateTime(ticket.updatedAt)}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Closed</p>
                <p className="mt-1 text-sm text-slate-700">{formatDateTime(ticket.closedAt)}</p>
              </div>
            </div>
          ) : null}

          <SelectField
            label="Contact"
            value={form.contactId}
            disabled={!customerId}
            placeholder={
              !customerId
                ? form.origin === 'project'
                  ? 'Select a project first'
                  : 'Select a customer first'
                : contactsQuery.isLoading
                  ? 'Loading…'
                  : 'No contact'
            }
            options={contactOptions}
            onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value }))}
            error={fieldErrors.contactId}
          />

          {form.origin === 'support' ? (
            <SelectField
              label="Ticket type"
              required
              value={form.ticketTypeId}
              placeholder={typesQuery.isLoading ? 'Loading…' : 'Select a type'}
              options={typeOptions}
              onChange={(e) => setForm((f) => ({ ...f, ticketTypeId: e.target.value }))}
              error={fieldErrors.ticketTypeId}
            />
          ) : null}

          <SelectField
            label="Type of ticket"
            required
            disabled={isEdit}
            value={form.origin}
            options={ORIGIN_OPTIONS}
            onChange={(e) =>
              setForm((f) =>
                e.target.value === 'project'
                  ? {
                      ...f,
                      origin: 'project',
                      customerId: '',
                      agreementId: '',
                      ticketTypeId: '',
                      contactId: '',
                    }
                  : {
                      ...f,
                      origin: 'support',
                      projectId: '',
                      phaseId: '',
                      contactId: '',
                    },
              )
            }
            error={fieldErrors.origin}
          />

          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Assignees</span>
            {selectedMembers.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {selectedMembers.map((member) => (
                  <span
                    key={member.id}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-1 pl-2.5 pr-1 text-xs font-medium text-brand-700"
                  >
                    {member.displayName}
                    <button
                      type="button"
                      onClick={() => toggleAssignee(member.id)}
                      aria-label={`Remove ${member.displayName}`}
                      className="flex h-4 w-4 items-center justify-center rounded-full text-brand-500 hover:bg-brand-100 hover:text-brand-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <input
              type="text"
              value={assigneeSearch}
              onChange={(e) => setAssigneeSearch(e.target.value)}
              placeholder="Search members…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200">
              {membersQuery.isLoading ? (
                <p className="px-3 py-2 text-sm text-slate-500">Loading members…</p>
              ) : filteredMembers.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-500">
                  {assigneeSearch.trim()
                    ? 'No matching members.'
                    : members.length === 0
                      ? 'No members available.'
                      : 'All members assigned.'}
                </p>
              ) : (
                filteredMembers.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => {
                      toggleAssignee(member.id);
                      setAssigneeSearch('');
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  >
                    {member.displayName}
                  </button>
                ))
              )}
            </div>
          </div>

          {isEdit && ticket && !publicToken && canEdit ? (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">Public ticket link</p>
                  <p className="mt-1 text-xs text-slate-400">
                    This ticket has no public link yet. Generate one to let anyone with the link
                    view the ticket and add hours for its assignees.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    regenerateLink.mutate(ticket.id, {
                      onSuccess: (updated) => setPublicToken(updated.publicToken),
                    })
                  }
                  isLoading={regenerateLink.isPending}
                >
                  Generate public link
                </Button>
              </div>
              {regenerateLink.isError ? (
                <p className="mt-2 text-xs text-red-600">
                  {mutationError(regenerateLink.error, 'Failed to generate the public link.')}
                </p>
              ) : null}
            </div>
          ) : null}

          {isEdit && publicToken ? (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">Public ticket link</p>
                  <p className="mt-1 truncate font-mono text-xs text-slate-500">
                    {`${window.location.origin}/t/${publicToken}`}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Anyone with this link can view the ticket and add hours for its assignees.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(`${window.location.origin}/t/${publicToken}`)
                        .then(() => {
                          setLinkCopied(true);
                          window.setTimeout(() => setLinkCopied(false), 2000);
                        });
                    }}
                  >
                    {linkCopied ? 'Copied!' : 'Copy link'}
                  </Button>
                  {canEdit ? (
                    <Button type="button" variant="ghost" onClick={() => setRegenerateOpen(true)}>
                      Regenerate
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </form>
        {isEdit && ticket ? (
          <div className={tab === 'hours' ? '' : 'hidden'}>
            <div className="rounded-lg border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
                <span className="text-sm font-medium text-slate-700">
                  Hours
                  <span className="ml-2 tabular-nums text-slate-500">{ticket.actualHours} hrs</span>
                </span>
                {ticket.timeEntryLocked ? (
                  <span className="text-xs font-medium text-rose-600">
                    Closed — time tracking locked
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    className="px-2.5 py-1 text-xs"
                    onClick={() => {
                      setEditingHour(null);
                      setHourModalOpen(true);
                    }}
                  >
                    Add hour
                  </Button>
                )}
              </div>
              {ticketHours.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2">
                  <input
                    type="search"
                    value={hourSearch}
                    onChange={(e) => setHourSearch(e.target.value)}
                    placeholder="Filter by date, member, category, class, notes, status…"
                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <select
                    value={hourStatusFilter}
                    onChange={(e) => setHourStatusFilter(e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    {BILLING_STATUS_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="divide-y divide-slate-100">
                {hoursQuery.isLoading ? (
                  <p className="px-3 py-2 text-sm text-slate-500">Loading hours…</p>
                ) : ticketHours.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-slate-500">No hours logged yet.</p>
                ) : filteredHours.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-slate-500">No hours match the filter.</p>
                ) : (
                  filteredHours.map((hour) => {
                    const status = billingStatusOf(hour);
                    return (
                      <button
                        key={hour.id}
                        type="button"
                        onClick={() => {
                          setEditingHour(hour);
                          setHourModalOpen(true);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">
                            <span className="text-slate-600">{formatDate(hour.workDate)}</span>
                            <Badge tone={status.tone}>
                              {status.detail ? `${status.label} · ${status.detail}` : status.label}
                            </Badge>
                          </span>
                          <span className="tabular-nums font-medium text-slate-800">
                            {hour.hours} hrs
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
                          <span className="font-medium text-slate-600">
                            {memberNames.get(hour.memberId) ?? '—'}
                          </span>
                          <span aria-hidden>·</span>
                          <span>{categoryNames.get(hour.serviceCategoryId) ?? '—'}</span>
                          <span aria-hidden>·</span>
                          <span>{classNames.get(hour.billingClassId) ?? '—'}</span>
                        </div>
                        {hour.notes ? (
                          <div className="mt-0.5 truncate text-xs text-slate-500">{hour.notes}</div>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : null}
        {isEdit && ticket ? (
          <div className={tab === 'history' ? '' : 'hidden'}>
            <HistoryPanel entityType="Ticket" entityId={ticket.id} enabled={tab === 'history'} />
          </div>
        ) : null}
      </Modal>
      {isEdit && ticket ? (
        <TimeEntryFormModal
          isOpen={hourModalOpen}
          entry={editingHour}
          initialMemberId={currentMemberId}
          initialWorkItem={
            ticket.origin === 'project'
              ? {
                  projectId: ticket.projectId,
                  phaseId: ticket.phaseId,
                  ticketId: ticket.id,
                  serviceCategoryId: ticket.defaultServiceCategoryId,
                  billingClassId: ticket.defaultBillingClassId,
                }
              : {
                  agreementId: ticket.agreementId,
                  ticketId: ticket.id,
                  serviceCategoryId: ticket.defaultServiceCategoryId,
                  billingClassId: ticket.defaultBillingClassId,
                }
          }
          onClose={() => {
            setHourModalOpen(false);
            setEditingHour(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete ticket"
        message={`Delete ticket ${ticket ? ticketDisplayId(ticket) : ''}? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
      <TicketDiagnosticsModal
        isOpen={diagnosticsOpen}
        ticketId={ticket?.id ?? null}
        onClose={() => setDiagnosticsOpen(false)}
      />
      <ConfirmDialog
        isOpen={regenerateOpen}
        title="Regenerate public link"
        message="Regenerate the public ticket link? The current link will stop working immediately."
        confirmLabel="Regenerate link"
        destructive
        isConfirming={regenerateLink.isPending}
        onConfirm={() => {
          if (ticket) {
            regenerateLink.mutate(ticket.id, {
              onSuccess: (updated) => {
                setPublicToken(updated.publicToken);
                setRegenerateOpen(false);
              },
            });
          }
        }}
        onCancel={() => setRegenerateOpen(false)}
      />
    </>
  );
}
